import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { useEffect, useState } from "react";
import DataNotFound from "../ui/loader/data-not-found";
import InnerLoader from "../ui/loader/inner-loader";
import { CustomDropdown } from "../ui/dropdown/CustomDropdown";
import { CustomDropdownButton } from "../ui/dropdown/CustomDropdownButton";
import { CustomDropdownMenu } from "../ui/dropdown/CustomDropdownMenu";
import { CustomDropdownMenuList } from "../ui/dropdown/CustomDropdownMenuList";
import { useApiCall } from "../custom-hooks/api-call";
import { ChartMainCard } from "./ChartsHeader";
import { TeamAvailability } from "./StatCardIcons";

// const PRODUCT_COLORS = {
//   Web: "#0c4b5e",
//   Logo: "#ffc107",
//   Animation: "#34a853",
//   Printing: "#6c757d",
//   "Animated Logo": "#ff5722",
//   SEO: "#9c27b0",
//   Branding: "#00bcd4",
//   "Social Media": "#ab47bc",
//   "Email Campaign": "#795548",
//   Referral: "#ffc107",
//   Website: "#fd3995",
//   Unknown: "#888888",
// };

const PRODUCT_COLORS = {
  Web: "#2563EB", // royal blue
  Website: "#1D4ED8", // deep blue

  Logo: "#F59E0B", // strong amber
  "Animated Logo": "#EA580C", // burnt orange

  Animation: "#7C3AED", // rich violet
  Printing: "#475569", // slate gray

  SEO: "#059669", // emerald green
  Branding: "#0EA5E9", // sky blue

  "Social Media": "#DB2777", // bold pink
  "Email Campaign": "#9333EA", // deep purple

  Referral: "#DC2626", // strong red

  Illustrative: "#14B8A6", // teal
  Stationery: "#A855F7", // violet purple
  Resume: "#F97316", // orange

  // NEW PRODUCTS
  "All In One": "#0F766E", // dark cyan
  Design: "#D97706", // golden brown
  Marketing: "#16A34A", // bright green
  Writing: "#C026D3", // magenta purple

  Unknown: "#6B7280", // neutral gray
};

export function LeadsByProduct({ sourceData = [], sourceLoading }) {
  const getProductColor = (product) => {
    return PRODUCT_COLORS[product] || "#888888";
  };

  const formattedSourceData = sourceData?.map((item) => ({
    name: item.product,
    value: Number(item?.count) || 0,
    color: getProductColor(item.product),
  }));
  return (
    <ChartMainCard
      Icon={<TeamAvailability />}
      iconColor={"bg-[#FEF3C7] text-[#D97706]"}
      heading="Product Lead Distribution"
      subHeading={"Count-based breakdown of leads per product"}
    >
      {sourceLoading ? (
        <InnerLoader />
      ) : (
        <div className="p-6 relative w-full h-full flex items-center">
          {formattedSourceData?.length > 0 ? (
            <div className="flex items-center gap-4 w-full ">
              <div className="flex-shrink-0 w-52 max-h-[320px] overflow-y-auto bg-gray-50 rounded-xl p-2 ">
                {[...formattedSourceData]
                  ?.sort((a, b) => b.value - a.value)
                  ?.map((eachData, idx) => {
                    const total = formattedSourceData?.reduce(
                      (a, b) => a + b.value,
                      0,
                    );
                    const percent = total
                      ? ((eachData.value / total) * 100).toFixed(0)
                      : 0;

                    return (
                      // <div key={idx} className="flex items-center gap-2 mb-4">
                      //   <span
                      //     className="w-3 h-3 rounded-full"
                      //     style={{ backgroundColor: eachData.color }}
                      //   ></span>
                      //   <div className="flex items-center gap-2 flex-1">
                      //     <div className="bg-gray-100 rounded-full flex justify-center items-center h-8 w-8">
                      //       <span className="text-lg">
                      //         {eachData?.name?.trim()?.[0]?.toUpperCase() ||
                      //           "?"}
                      //       </span>
                      //     </div>
                      //     <div>
                      //       <h6 className="font-semibold text-sm">
                      //         {eachData.name}
                      //       </h6>
                      //       <span className="text-xs text-gray-600">
                      //         Leads: {eachData.value}
                      //       </span>
                      //     </div>
                      //   </div>
                      // </div>
                      <div
                        key={idx}
                        className="flex items-center gap-2 mb-4 bg-gray-100 rounded p-1"
                      >
                        <span
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: eachData.color }}
                        ></span>
                        <div className="flex items-center gap-2 flex-1">
                          <div className="bg-gray-100 rounded-full flex justify-center items-center h-8 w-8">
                            <span className="text-lg">
                              {eachData?.name?.trim()?.[0]?.toUpperCase() || "?"}
                            </span>
                          </div>
                          <div>
                            <h6 className="font-semibold text-sm">
                              {eachData?.name}
                            </h6>
                            <span className="text-xs text-gray-600">
                              Leads: {eachData?.value}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>

              <div className="flex-1 relative w-full">
                {/* Center Label */}
                {(() => {
                  const total = formattedSourceData?.reduce(
                    (a, b) => a + b.value,
                    0,
                  );
                  const topItem = [...formattedSourceData]?.sort(
                    (a, b) => b.value - a.value,
                  )[0];

                  if (!topItem || total === 0) return null;

                  const percent = ((topItem.value / total) * 100).toFixed(0);

                  return (
                    <div className="absolute inset-0 top-[72px] z-10 bottom-auto m-auto flex flex-col items-center justify-center pointer-events-none">
                      <p className="text-sm text-gray-500">{topItem.name}</p>
                      <p className="text-xl font-bold text-gray-900">
                        {percent}%
                      </p>
                    </div>
                  );
                })()}

                <ResponsiveContainer
                  width="100%"
                  className={"relative z-20"}
                  height={192}
                >
                  <PieChart>
                    <Pie
                      data={formattedSourceData}
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={95}
                      dataKey="value"
                    >
                      {formattedSourceData?.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : (
            <DataNotFound title={"Products"} />
          )}
        </div>
      )}
    </ChartMainCard>
  );
}
